import { create } from 'zustand'
import {
  changePassword as postChangePassword,
  fetchAuthMe,
  loginWithPassword as postLoginWithPassword,
  logoutSession,
} from '@/services/api/auth'
import type { AuthMeResponse } from '@/services/api/auth'

export type AuthMode = 'dev_header' | 'token' | 'password'

const AUTH_SESSION_STORAGE_KEY = 'after_sale_auth_session'
const AUTH_LOGGED_OUT_STORAGE_KEY = 'after_sale_auth_logged_out'
const AUTH_MODES: AuthMode[] = ['dev_header', 'token', 'password']
const DEFAULT_AUTH_MODE = normalizeAuthMode(import.meta.env.VITE_AUTH_MODE)
const DEFAULT_DEV_ACTOR = import.meta.env.VITE_DEV_ACTOR || 'ops_admin'
const DEFAULT_DEV_ROLES = parseRoles(import.meta.env.VITE_DEV_ROLES || 'admin,service_agent')
const ENV_API_TOKEN = (import.meta.env.VITE_API_TOKEN || '').trim()

interface StoredAuthSession {
  authMode: AuthMode
  actor?: string
  roles?: string[]
  apiToken?: string
}

interface AuthState {
  actor: string
  roles: string[]
  authMode: AuthMode
  apiToken: string
  permissions: string[]
  groupScopeDetails: AuthMeResponse['scopes']['group_details']
  mustChangePassword: boolean
  isAuthenticated: boolean
  authInitialized: boolean
  authLoading: boolean
  authError: string | null
  setAuth: (actor: string, roles: string[]) => void
  clearAuth: () => void
  loginWithDevHeader: (actor: string, roles: string[]) => Promise<void>
  loginWithToken: (apiToken: string) => Promise<void>
  loginWithPassword: (account: string, password: string) => Promise<void>
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>
  logout: () => Promise<void>
  hasPermission: (code: string) => boolean
  initializeAuth: () => Promise<void>
}

function parseRoles(value: string) {
  return value
    .split(',')
    .map((item: string) => item.trim())
    .filter(Boolean)
}

function authErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Failed to initialize auth'
}

function isAuthMode(value: unknown): value is AuthMode {
  return typeof value === 'string' && AUTH_MODES.includes(value as AuthMode)
}

function normalizeAuthMode(value: unknown): AuthMode {
  return isAuthMode(value) ? value : 'dev_header'
}

function readStoredSession(): StoredAuthSession | null {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredAuthSession
    if (!isAuthMode(parsed.authMode)) {
      window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
      return null
    }
    if (parsed.authMode !== DEFAULT_AUTH_MODE) {
      window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
    return null
  }
}

function writeStoredSession(session: StoredAuthSession) {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(AUTH_LOGGED_OUT_STORAGE_KEY)
  window.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session))
}

function clearStoredSession() {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
}

function markLoggedOut() {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(AUTH_LOGGED_OUT_STORAGE_KEY, 'true')
}

function wasLoggedOut() {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(AUTH_LOGGED_OUT_STORAGE_KEY) === 'true'
}

const storedSession = readStoredSession()
const blockedByLogout = wasLoggedOut()
const initialAuthMode = storedSession?.authMode || DEFAULT_AUTH_MODE

/**
 * 前端身份状态。
 *
 * - dev_header 模式：登录页输入 actor / roles，并通过 /api/auth/me 校验，用于本地开发。
 * - token 模式：登录页输入 API token，或使用 VITE_API_TOKEN 作为部署级兜底。
 * - password 模式：登录页输入用户名密码，服务端通过 HttpOnly Cookie 维持会话。
 */
export const useAuthStore = create<AuthState>((set, get) => ({
  actor: storedSession?.actor || (!blockedByLogout && initialAuthMode === 'dev_header' ? DEFAULT_DEV_ACTOR : ''),
  roles: storedSession?.roles || (!blockedByLogout && initialAuthMode === 'dev_header' ? DEFAULT_DEV_ROLES : []),
  authMode: initialAuthMode,
  apiToken: storedSession?.apiToken || '',
  permissions: [],
  groupScopeDetails: [],
  mustChangePassword: false,
  isAuthenticated: false,
  authInitialized: false,
  authLoading: false,
  authError: null,
  setAuth: (actor, roles) => set({ actor, roles, isAuthenticated: true }),
  clearAuth: () => {
    clearStoredSession()
    markLoggedOut()
    set({
      actor: '',
      roles: [],
      apiToken: '',
      permissions: [],
      groupScopeDetails: [],
      mustChangePassword: false,
      isAuthenticated: false,
      authInitialized: true,
      authError: null,
    })
  },
  loginWithDevHeader: async (actor, roles) => {
    const normalizedActor = actor.trim()
    const normalizedRoles = roles.map((role) => role.trim()).filter(Boolean)
    set({
      authMode: 'dev_header',
      actor: normalizedActor,
      roles: normalizedRoles,
      apiToken: '',
      permissions: [],
      groupScopeDetails: [],
      mustChangePassword: false,
      isAuthenticated: false,
      authInitialized: false,
      authLoading: true,
      authError: null,
    })
    try {
      const me = await fetchAuthMe()
      writeStoredSession({ authMode: 'dev_header', actor: normalizedActor, roles: normalizedRoles })
      set({
        actor: me.account,
        roles: me.roles,
        permissions: me.permissions,
        groupScopeDetails: me.scopes.group_details || [],
        mustChangePassword: Boolean(me.must_change_password),
        isAuthenticated: true,
        authInitialized: true,
        authLoading: false,
      })
    } catch (error) {
      clearStoredSession()
      set({
        actor: normalizedActor,
        roles: normalizedRoles,
        permissions: [],
        groupScopeDetails: [],
        mustChangePassword: false,
        isAuthenticated: false,
        authInitialized: true,
        authLoading: false,
        authError: authErrorMessage(error),
      })
      throw error
    }
  },
  loginWithToken: async (apiToken) => {
    const normalizedToken = apiToken.trim()
    set({
      authMode: 'token',
      actor: '',
      roles: [],
      apiToken: normalizedToken,
      permissions: [],
      groupScopeDetails: [],
      mustChangePassword: false,
      isAuthenticated: false,
      authInitialized: false,
      authLoading: true,
      authError: null,
    })
    try {
      const me = await fetchAuthMe()
      writeStoredSession({ authMode: 'token', apiToken: normalizedToken })
      set({
        actor: me.account,
        roles: me.roles,
        permissions: me.permissions,
        groupScopeDetails: me.scopes.group_details || [],
        mustChangePassword: Boolean(me.must_change_password),
        isAuthenticated: true,
        authInitialized: true,
        authLoading: false,
      })
    } catch (error) {
      clearStoredSession()
      set({
        actor: '',
        roles: [],
        apiToken: '',
        permissions: [],
        groupScopeDetails: [],
        mustChangePassword: false,
        isAuthenticated: false,
        authInitialized: true,
        authLoading: false,
        authError: authErrorMessage(error),
      })
      throw error
    }
  },
  loginWithPassword: async (account, password) => {
    const normalizedAccount = account.trim()
    set({
      authMode: 'password',
      actor: normalizedAccount,
      roles: [],
      apiToken: '',
      permissions: [],
      groupScopeDetails: [],
      mustChangePassword: false,
      isAuthenticated: false,
      authInitialized: false,
      authLoading: true,
      authError: null,
    })
    try {
      const me = await postLoginWithPassword({ account: normalizedAccount, password })
      writeStoredSession({ authMode: 'password' })
      set({
        actor: me.account,
        roles: me.roles,
        permissions: me.permissions,
        groupScopeDetails: me.scopes.group_details || [],
        mustChangePassword: Boolean(me.must_change_password),
        isAuthenticated: true,
        authInitialized: true,
        authLoading: false,
      })
    } catch (error) {
      clearStoredSession()
      set({
        actor: '',
        roles: [],
        apiToken: '',
        permissions: [],
        groupScopeDetails: [],
        mustChangePassword: false,
        isAuthenticated: false,
        authInitialized: true,
        authLoading: false,
        authError: authErrorMessage(error),
      })
      throw error
    }
  },
  changePassword: async (oldPassword, newPassword) => {
    set({ authLoading: true, authError: null })
    try {
      await postChangePassword({ old_password: oldPassword, new_password: newPassword })
      const me = await fetchAuthMe()
      set({
        actor: me.account,
        roles: me.roles,
        permissions: me.permissions,
        groupScopeDetails: me.scopes.group_details || [],
        mustChangePassword: Boolean(me.must_change_password),
        isAuthenticated: true,
        authInitialized: true,
        authLoading: false,
      })
    } catch (error) {
      set({
        authLoading: false,
        authError: authErrorMessage(error),
      })
      throw error
    }
  },
  logout: async () => {
    if (get().authMode === 'password') {
      try {
        await logoutSession()
      } catch {
        // Local state must still be cleared when the server session is already gone.
      }
    }
    clearStoredSession()
    markLoggedOut()
    set({
      actor: '',
      roles: [],
      apiToken: '',
      permissions: [],
      groupScopeDetails: [],
      mustChangePassword: false,
      isAuthenticated: false,
      authInitialized: true,
      authLoading: false,
      authError: null,
    })
  },
  hasPermission: (code: string) => get().permissions.includes(code),
  initializeAuth: async () => {
    if (get().authLoading || get().isAuthenticated) return
    const state = get()
    const fallbackToken = state.authMode === 'token' && !wasLoggedOut() ? ENV_API_TOKEN : ''
    const hasCredentials = state.authMode === 'token'
      ? Boolean(state.apiToken || fallbackToken)
      : state.authMode === 'password'
        ? !wasLoggedOut()
        : Boolean(state.actor)
    if (!hasCredentials) {
      set({ authInitialized: true, authError: null, permissions: [], groupScopeDetails: [] })
      return
    }
    set({ authLoading: true, authError: null })
    try {
      const me = await fetchAuthMe()
      set({
        actor: me.account,
        roles: me.roles,
        permissions: me.permissions,
        groupScopeDetails: me.scopes.group_details || [],
        mustChangePassword: Boolean(me.must_change_password),
        isAuthenticated: true,
        authInitialized: true,
        authLoading: false,
      })
    } catch (error) {
      clearStoredSession()
      set({
        actor: '',
        roles: [],
        apiToken: '',
        permissions: [],
        groupScopeDetails: [],
        mustChangePassword: false,
        isAuthenticated: false,
        authInitialized: true,
        authLoading: false,
        authError: authErrorMessage(error),
      })
    }
  },
}))

if (import.meta.env.DEV && typeof window !== 'undefined') {
  ;(window as Window & { __AUTH_STORE__?: typeof useAuthStore }).__AUTH_STORE__ = useAuthStore
}
